/**
 * Axios client dedicated to the `ai_training` FastAPI service.
 *
 * Talks to https://ai.mutabletech.co.ke/v1/* in production (configured
 * via VITE_AI_TRAINING_BASE_URL). Independent of the main `server_api`
 * axios instance because:
 *   - Different host / origin.
 *   - Different auth — a trainer JWT issued by /v1/auth/login, NOT the
 *     LMS session cookie.
 *
 * The interceptor reads the JWT from localStorage (key
 * `ai_trainer_token`). The AiTrainerAuthContext owns the read/write
 * lifecycle; this client is a stateless transport layer.
 */
import axios, { type AxiosInstance } from 'axios';

const env = (import.meta as any).env || {};

const BASE_URL: string =
  env.VITE_AI_TRAINING_BASE_URL || 'https://ai.mutabletech.co.ke/v1';

export const AI_TRAINER_TOKEN_KEY = 'ai_trainer_token';

export const aiTrainerClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 60_000,
});

aiTrainerClient.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined'
    ? window.localStorage.getItem(AI_TRAINER_TOKEN_KEY)
    : null;
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 → wipe token so the AuthContext gates immediately.
aiTrainerClient.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== 'undefined') {
      window.localStorage.removeItem(AI_TRAINER_TOKEN_KEY);
    }
    return Promise.reject(err);
  },
);

export const AI_TRAINER_BASE_URL = BASE_URL;
